import { CommonModule } from '@angular/common';
import { Component, computed, effect, input, OnDestroy, output, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { interval, Subscription, takeWhile } from 'rxjs';

@Component({
  selector: 'aura-input-code-verification',
  templateUrl: './code-verification.component.html',
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule],
})
export class AuraInputCodeVerificationComponent implements OnDestroy {
  label = input<string>('Code de vérification');
  length = input<number>(8);
  triggerResend = input<number>(0);
  type = input<'text' | 'password' | 'number'>('text');

  codeChange = output<string>();
  complete = output<string>();
  resend = output();

  inputs = Array.from({ length: this.length() }, () => signal(''));
  formControls: FormControl[] = Array.from({ length: this.length() }, () => new FormControl(''));
  indices = Array.from({ length: this.length() }, (_, i) => i);
  private countdownSub?: Subscription;

  private readonly countdownStart = 20;
  private readonly remaining = signal(this.countdownStart);
  readonly isDisabled = computed(() => this.remaining() > 0);

  readonly buttonLabel = computed(() => (this.isDisabled() ? `Renvoyer (${this.remaining()}s)` : 'Renvoyer'));

  constructor() {
    this.startCountdown();

    effect(() => {
      const count = this.triggerResend();
      // ignore the initial 0 value
      if (count > 0) {
        this.resetCountdown();
        this.resend.emit();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  private startCountdown(): void {
    this.stopCountdown();

    this.countdownSub = interval(1000)
      .pipe(takeWhile(() => this.remaining() > 0))
      .subscribe(() => this.remaining.update((v) => v - 1));
  }

  onResend(): void {
    if (this.isDisabled()) return;
    this.resetCountdown();
    this.resend.emit();
  }

  private stopCountdown(): void {
    this.countdownSub?.unsubscribe();
    this.countdownSub = undefined;
  }

  private resetCountdown(): void {
    this.remaining.set(60);
    this.startCountdown();
  }

  isValid = this.inputs.map((s, i) =>
    computed(() => {
      const val = s();
      if (!val) return true; // empty is not invalid
      return i < 6 ? /^[0-9]$/.test(val) : /^[A-Z]$/.test(val);
    }),
  );

  get code(): string {
    return this.inputs.map((s) => s()).join('');
  }

  onInput(event: Event, index: number) {
    const inputEl = event.target as HTMLInputElement;
    const value = (inputEl.value || '').toUpperCase();

    this.inputs[index].set(value);

    if (value && index < this.length() - 1) {
      const nextInput = document.getElementById(`code-input-${index + 1}`) as HTMLInputElement | null;
      nextInput?.focus();
    }

    this.codeChange.emit(this.code);

    if (this.code.length === this.length() && this.allValid()) {
      this.complete.emit(this.code);
    }
  }

  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key === 'Backspace' && !this.inputs[index]()) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      prevInput?.focus();
    }
  }

  allValid(): boolean {
    return this.isValid.every((v) => v());
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text')?.toUpperCase() ?? '';
    const allowed = pastedData.replace(/[^0-9A-Z]/g, '');

    if (!allowed) return;

    for (let i = 0; i < this.length(); i++) {
      const char = allowed[i];
      if (char) {
        this.inputs[i].set(char);
        const inputEl = document.getElementById(`code-input-${i}`) as HTMLInputElement;
        if (inputEl) inputEl.value = char;
      } else {
        this.inputs[i].set('');
        const inputEl = document.getElementById(`code-input-${i}`) as HTMLInputElement;
        if (inputEl) inputEl.value = '';
      }
    }

    this.codeChange.emit(this.code);
    if (this.code.length === this.length() && this.allValid()) {
      this.complete.emit(this.code);
    }
  }
}
